import React, { useEffect, useState, useMemo, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const PfForm12Report = () => {
  const dispatch = useDispatch();

  // Setup session state from localStorage
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // Lists and loading states
  const [, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Dual View Mode: "register" (Employee Contribution Data Grid) vs "statutory" (Statutory Return Form 12)
  const [activeView, setActiveView] = useState("register");

  // Pagination for register table
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(15);

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial base listings
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when firm, month, or year changes
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear]);

  const loadReportData = async (firmId, monthName, year) => {
    setIsLoading(true);
    try {
      if (monthName && year) {
        const fd = new FormData();
        fd.append("F_FirmMaster", firmId);
        fd.append("PayrollMonth", monthName);
        fd.append("PayrollYear", year);

        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetPFForm12/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );

        let parsedData = null;
        if (records && records.length > 0) {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey !== undefined && firstKey !== null) {
            const jsonString = records.map(row => row[firstKey] || "").join("");
            if (jsonString) {
              const rawObj = JSON.parse(jsonString);
              parsedData = rawObj.data || rawObj;
            }
          }
        }
        setReportData(parsedData);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Find Firm Code No. (PF Code)
  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  const getFirmAddress = () => {
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.OfficeAddress || mainBranch.CoAddress || "N/A";
      }
    } catch (e) {
      console.error("Error parsing branch data:", e);
    }
    return "N/A";
  };

  const records = reportData?.Records || reportData?.Employees || [];

  const processedRows = useMemo(() => {
    return records.map((rec, index) => {
      const basic = parseFloat(rec.Basic || rec.BasicWages || rec.Wages || 0);
      const paidDays = parseFloat(rec.PaidDays || rec.PaidDay || 0);
      const gross = parseFloat(rec.Gross || rec.GrossWages || rec.GrossAmount || 0);
      const eligibleSalary = parseFloat(rec.EligibleSalary || rec.EligibleWages || 0);
      const employeePf = parseFloat(rec.EmployeePf || rec.PF || 0);
      const emplrAc1 = parseFloat(rec.EmplrAc1 || rec.EmplrPF || 0);
      const emplrAc10 = parseFloat(rec.EmplrAc10 || rec.Pension || 0);
      const totalPf = employeePf + emplrAc1 + emplrAc10;

      return {
        sno: index + 1,
        acNo: rec.AcNo || rec.SerialNo || "",
        uan: rec.Uan || rec.UAN || "",
        name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
        fatherName: (rec.FatherName || "").toUpperCase(),
        basic,
        paidDays,
        gross,
        eligibleSalary,
        employeePf,
        emplrAc1,
        emplrAc10,
        totalPf
      };
    });
  }, [records]);

  // Search filter
  const filteredRows = useMemo(() => {
    const query = searchTerm.toLowerCase().trim();
    if (!query) return processedRows;
    return processedRows.filter(row => 
      row.name.toLowerCase().includes(query) || 
      row.fatherName.toLowerCase().includes(query) || 
      String(row.uan).toLowerCase().includes(query) || 
      String(row.acNo).toLowerCase().includes(query)
    );
  }, [processedRows, searchTerm]);

  // Totals calculations
  const totals = useMemo(() => {
    let totalBasic = 0;
    let totalPaidDays = 0;
    let totalGross = 0;
    let totalEligible = 0;
    let totalEmpPf = 0;
    let totalAc1 = 0;
    let totalAc10 = 0;
    let grandTotalPf = 0;

    filteredRows.forEach(row => {
      totalBasic += row.basic;
      totalPaidDays += row.paidDays;
      totalGross += row.gross;
      totalEligible += row.eligibleSalary;
      totalEmpPf += row.employeePf;
      totalAc1 += row.emplrAc1;
      totalAc10 += row.emplrAc10;
      grandTotalPf += row.totalPf;
    });

    return { totalBasic, totalPaidDays, totalGross, totalEligible, totalEmpPf, totalAc1, totalAc10, grandTotalPf };
  }, [filteredRows]);

  // Pagination for data grid
  const paginatedRows = useMemo(() => {
    if (tablePageSize === "all") return filteredRows;
    const size = parseInt(tablePageSize, 10);
    const start = (tablePage - 1) * size;
    return filteredRows.slice(start, start + size);
  }, [filteredRows, tablePage, tablePageSize]);

  const totalPages = tablePageSize === "all" ? 1 : Math.ceil(filteredRows.length / parseInt(tablePageSize, 10)) || 1;

  useEffect(() => {
    setTablePage(1);
  }, [filteredRows.length, tablePageSize]);

  // ─── COMPUTE SUMMARY DATA ───
  const summary = reportData?.Summary || {
    PfApplicableWages: 0,
    PfApplicableNos: 0,
    PfExemptWages: 0,
    ExemptWages: 0,
    ExemptNos: 0,
    AbsentNos: 0,
    TotalSummaryWages: 0,
    TotalSummaryNos: 0
  };

  const pfApplicableWages = parseFloat(summary.PfApplicableWages || 0);
  const pfApplicableNos = parseInt(summary.PfApplicableNos || 0);
  const pfExemptWages = parseFloat(summary.PfExemptWages || 0);
  const exemptWages = parseFloat(summary.ExemptWages || 0);
  const exemptNos = parseInt(summary.ExemptNos || 0);
  const absentNos = parseInt(summary.AbsentNos || 0);

  const totalSummaryWages = pfApplicableWages + pfExemptWages + exemptWages;
  const totalSummaryNos = pfApplicableNos + exemptNos + absentNos;

  // Contributions formulas
  const empPlusEmplrAc1 = totals.totalEmpPf + totals.totalAc1;
  const pensionAc10 = totals.totalAc10;
  const adminAc2 = Math.max(Math.round(totals.totalEligible * 0.005), 500);
  const edliAc21 = Math.max(Math.round(totals.totalEligible * 0.005), 200);
  const insuranceAc22 = 0;
  const rightTotal = empPlusEmplrAc1 + pensionAc10 + adminAc2 + edliAc21 + insuranceAc22;

  // Excel Export
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("PF Form 12");

      worksheet.addRow([sessionFirm?.Name?.toUpperCase() || ""]);
      worksheet.addRow([`EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF ${selectedMonth?.toUpperCase()} ${selectedYear}`]);
      worksheet.addRow([`CODE NO. : ${getFirmPfNo()}`, "", "", "", "", "", "", "FORM NO.12"]);
      worksheet.addRow([]);

      const headerRow1 = worksheet.addRow(["Sr. No.", "AC No", "EMPLOYEE NAME", "BASIC", "GROSS WAGES", "EMPLOYEE P.F.", "EMPLOYER'S SHARE", "", "TOTAL P.F."]);
      const headerRow2 = worksheet.addRow(["", "UAN", "FATHER NAME", "PAID DAYS", "ELIGIBLE SALARY", "", "A/C.1 P.F.", "A/C.10 PENSION.", ""]);

      [headerRow1, headerRow2].forEach(row => {
        row.font = { bold: true, color: { argb: "FFFFFFFF" } };
        row.eachCell(cell => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1E3C72" }
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } }
          };
        });
      });

      worksheet.mergeCells("A5:A6");
      worksheet.mergeCells("F5:F6");
      worksheet.mergeCells("G5:H5");
      worksheet.mergeCells("I5:I6");

      filteredRows.forEach((row) => {
        worksheet.addRow([
          row.sno,
          row.acNo,
          row.name,
          row.basic,
          row.gross,
          row.employeePf,
          row.emplrAc1,
          row.emplrAc10,
          row.totalPf
        ]);
        worksheet.addRow([
          "",
          row.uan,
          row.fatherName,
          row.paidDays,
          row.eligibleSalary,
          "",
          "",
          "",
          ""
        ]);
      });

      // Totals row
      worksheet.addRow([]);
      const totalRow = worksheet.addRow([
        "TOTALS",
        "",
        "",
        totals.totalBasic,
        totals.totalGross,
        totals.totalEmpPf,
        totals.totalAc1,
        totals.totalAc10,
        totals.grandTotalPf
      ]);
      totalRow.font = { bold: true };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PF_Form12_Report_${selectedMonth}_${selectedYear}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Excel export failed.");
    }
  };

  // PDF Export
  const exportToPDF = () => {
    if (filteredRows.length === 0) {
      alert("No data available to export.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");

    pdf.setFontSize(11);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 105, 12, { align: "center" });
    pdf.setFontSize(9);
    pdf.text(`EMPLOYEES PROVIDENT FUND REPORT FOR THE MONTH OF ${selectedMonth?.toUpperCase()} ${selectedYear}`, 105, 17, { align: "center" });

    pdf.setFontSize(8.5);
    pdf.text(`CODE NO. : ${getFirmPfNo()}`, 14, 23);
    pdf.text("FORM NO. 12 (REVISED)", 196, 23, { align: "right" });

    const tableBody = [];
    filteredRows.forEach(row => {
      tableBody.push([
        row.sno,
        row.acNo,
        row.name,
        row.basic.toFixed(2),
        row.gross.toFixed(2),
        row.employeePf,
        row.emplrAc1,
        row.emplrAc10
      ]);
      tableBody.push([
        "",
        { content: row.uan, styles: { textColor: [100, 100, 100] } },
        { content: row.fatherName, styles: { textColor: [120, 120, 120] } },
        row.paidDays.toFixed(2),
        row.eligibleSalary.toFixed(2),
        "",
        "",
        ""
      ]);
    });

    tableBody.push([
      { content: "TOTALS", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
      { content: totals.totalBasic.toFixed(2), styles: { fontStyle: "bold" } },
      { content: totals.totalGross.toFixed(2), styles: { fontStyle: "bold" } },
      { content: totals.totalEmpPf.toString(), styles: { fontStyle: "bold" } },
      { content: totals.totalAc1.toString(), styles: { fontStyle: "bold" } },
      { content: totals.totalAc10.toString(), styles: { fontStyle: "bold" } }
    ]);

    autoTable(pdf, {
      head: [
        [
          { content: "Sr.\nNo.", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
          { content: "AC No", styles: { halign: "center" } },
          { content: "EMPLOYEE NAME", styles: { halign: "left" } },
          { content: "BASIC", styles: { halign: "right" } },
          { content: "GROSS WAGES", styles: { halign: "right" } },
          { content: "EMPLOYEE\nP.F.", rowSpan: 2, styles: { valign: "middle", halign: "center" } },
          { content: "EMPLOYER'S SHARE", colSpan: 2, styles: { halign: "center" } }
        ],
        [
          { content: "UAN", styles: { halign: "center" } },
          { content: "FATHER NAME", styles: { halign: "left" } },
          { content: "PAID DAYS", styles: { halign: "right" } },
          { content: "ELIGIBLE SALARY", styles: { halign: "right" } },
          { content: "A/C.1\nP.F.", styles: { halign: "right" } },
          { content: "A/C.10\nPENSION.", styles: { halign: "right" } }
        ]
      ],
      body: tableBody,
      startY: 27,
      styles: {
        fontSize: 7.5,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [30, 60, 114],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        lineColor: [0, 0, 0],
        lineWidth: 0.1
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 10 },
        1: { halign: "center", cellWidth: 26 },
        2: { halign: "left", cellWidth: 50 },
        3: { halign: "right", cellWidth: 22 },
        4: { halign: "right", cellWidth: 26 },
        5: { halign: "right", cellWidth: 18 },
        6: { halign: "right", cellWidth: 18 },
        7: { halign: "right", cellWidth: 18 }
      },
      theme: "grid"
    });

    const finalY = pdf.previousAutoTable.finalY || 150;

    // Wages Summary
    autoTable(pdf, {
      head: [["", "WAGES", "NOS"]],
      body: [
        ["PF Applicable", Math.round(pfApplicableWages).toString(), pfApplicableNos.toString()],
        ["PF Exempt", Math.round(pfExemptWages).toString(), ""],
        ["Exempt", Math.round(exemptWages).toString(), exemptNos.toString()],
        ["Absent", "0", absentNos.toString()],
        [{ content: "Total :", styles: { fontStyle: "bold" } }, { content: Math.round(totalSummaryWages).toString(), styles: { fontStyle: "bold" } }, { content: totalSummaryNos.toString(), styles: { fontStyle: "bold" } }]
      ],
      startY: finalY + 8,
      margin: { right: 108, left: 14 },
      styles: { fontSize: 7, cellPadding: 1.5, lineColor: [0,0,0], lineWidth: 0.05 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0,0,0], fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 32 }, 1: { halign: "right", cellWidth: 28 }, 2: { halign: "right", cellWidth: 18 } }
    });

    // Contributions Summary
    autoTable(pdf, {
      body: [
        ["Employer+ee Share in PF Contribution A/c No. 1", empPlusEmplrAc1.toFixed(2)],
        ["Employers shares in pension fund A/C No.10", pensionAc10.toFixed(2)],
        ["ADM Charges A/c No. 2", adminAc2.toFixed(2)],
        ["EDLI A/C No. 21", edliAc21.toFixed(2)],
        ["Link Insurences Adm. Charges A/c No. 22", insuranceAc22.toFixed(2)],
        [{ content: "Total", styles: { fontStyle: "bold" } }, { content: rightTotal.toFixed(2), styles: { fontStyle: "bold" } }]
      ],
      startY: finalY + 8,
      margin: { left: 110, right: 14 },
      styles: { fontSize: 7, cellPadding: 1.5, lineColor: [0,0,0], lineWidth: 0.05 },
      columnStyles: { 0: { cellWidth: 64 }, 1: { halign: "right", cellWidth: 22 } }
    });

    pdf.save(`PF_Form12_Report_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-form12-container, #print-form12-container * {
        visibility: visible !important;
      }
      #print-form12-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
      }
      .no-print {
        display: none !important;
      }
      .form12-print-table {
        border-collapse: collapse;
        width: 100%;
        margin-top: 10px;
      }
      .form12-print-table th, .form12-print-table td {
        border: 1px solid #000;
        padding: 4px 6px;
        font-size: 10px;
      }
      .form12-print-table th {
        background: #f2f2f2;
        font-weight: bold;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="PF Form 12" motherMenu="Reports" pageContent="Monthly Return of EPF Contributions & Dues" />
      </div>

      {/* Filter Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-3 p-md-4">
          <Row className="align-items-end g-3">
            {/* Firm Dropdown */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year Dropdown */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search Filter */}
            <Col lg={4} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Search by name, father name, card, UAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a <strong>Firm</strong>, <strong>Month</strong>, and <strong>Year</strong> from the filters above or from the <strong>Dashboard Home</strong> to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
          <Card.Body className="text-center p-5">
            <div className="spinner-border text-primary mb-3" role="status" />
            <h6 className="text-dark fw-bold">Loading PF Form 12 Contributions...</h6>
            <small className="text-muted">Fetching member contribution ledger and account dues</small>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* ─── DUAL VIEW SWITCHER TABS & ACTION BUTTONS ─── */}
          <div className="no-print d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            {/* View Switcher Buttons */}
            <div className="d-flex gap-2 p-1 bg-white border rounded shadow-xs" role="group">
              <Button
                size="sm"
                className="px-3 fw-semibold fs-13"
                style={{
                  borderRadius: "6px",
                  backgroundColor: activeView === "register" ? "#1e3c72" : "#ffffff",
                  color: activeView === "register" ? "#ffffff" : "#334155",
                  border: activeView === "register" ? "1px solid #1e3c72" : "1px solid #cbd5e1",
                  boxShadow: activeView === "register" ? "0 2px 4px rgba(30,60,114,0.25)" : "none"
                }}
                onClick={() => setActiveView("register")}
              >
                📋 Employee Contribution Register
                <span
                  className="ms-2 px-2 py-0 rounded-pill fw-bold"
                  style={{
                    fontSize: "11px",
                    backgroundColor: activeView === "register" ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                    color: activeView === "register" ? "#ffffff" : "#1e293b"
                  }}
                >
                  {filteredRows.length}
                </span>
              </Button>
              <Button
                size="sm"
                className="px-3 fw-semibold fs-13"
                style={{
                  borderRadius: "6px",
                  backgroundColor: activeView === "statutory" ? "#1e3c72" : "#ffffff",
                  color: activeView === "statutory" ? "#ffffff" : "#334155",
                  border: activeView === "statutory" ? "1px solid #1e3c72" : "1px solid #cbd5e1",
                  boxShadow: activeView === "statutory" ? "0 2px 4px rgba(30,60,114,0.25)" : "none"
                }}
                onClick={() => setActiveView("statutory")}
              >
                🏛️ Statutory Form 12 Statement & Summary
              </Button>
            </div>

            {/* Global Actions */}
            <div className="d-flex gap-2">
              <Button
                size="sm"
                className="px-3 fw-semibold fs-12 shadow-xs"
                style={{ borderRadius: "6px", backgroundColor: "#15803d", color: "#ffffff", border: "none" }}
                onClick={exportToExcel}
              >
                <i className="fa fa-file-excel-o me-1 text-white" /> Export Excel
              </Button>
              <Button
                size="sm"
                className="px-3 fw-semibold fs-12 shadow-xs"
                style={{ borderRadius: "6px", backgroundColor: "#dc2626", color: "#ffffff", border: "none" }}
                onClick={exportToPDF}
              >
                <i className="fa fa-file-pdf-o me-1 text-white" /> Download PDF
              </Button>
              <Button
                size="sm"
                className="px-3 fw-semibold fs-12 shadow-xs"
                style={{ borderRadius: "6px", backgroundColor: "#334155", color: "#ffffff", border: "none" }}
                onClick={() => window.print()}
              >
                <i className="fa fa-print me-1 text-white" /> Print Form
              </Button>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB 1: 📋 EMPLOYEE CONTRIBUTION REGISTER (DATA GRID VIEW)  */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeView === "register" && (
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16">
                    PF Form 12 — Employee Contribution Register
                  </h5>
                  <small className="text-muted fs-12">
                    Firm: <strong>{sessionFirm?.Name}</strong> &nbsp;|&nbsp; PF Code: <strong>{getFirmPfNo()}</strong> &nbsp;|&nbsp; Period: <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong>
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted fs-12">Show:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "80px", borderRadius: "5px" }}
                    value={tablePageSize}
                    onChange={(e) => {
                      setTablePageSize(e.target.value);
                      setTablePage(1);
                    }}
                  >
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 text-nowrap" style={{ fontSize: "12px" }}>
                    <thead style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                      <tr>
                        <th className="text-center py-2 px-3" style={{ width: "50px" }}>#</th>
                        <th className="py-2">Card / AC No</th>
                        <th className="py-2">Employee Name & Father</th>
                        <th className="py-2">UAN</th>
                        <th className="text-end py-2">Paid Days</th>
                        <th className="text-end py-2">Basic (₹)</th>
                        <th className="text-end py-2">Gross (₹)</th>
                        <th className="text-end py-2">Eligible (₹)</th>
                        <th className="text-end py-2">Emp PF 12% (₹)</th>
                        <th className="text-end py-2">Emplr Ac 1 (₹)</th>
                        <th className="text-end py-2">Pension Ac 10 (₹)</th>
                        <th className="text-end py-2">Total PF (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="text-center py-5 text-muted">
                            No employees under PF coverage found for this period.
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((row, index) => {
                          const globalIndex = tablePageSize === "all" 
                            ? index 
                            : (tablePage - 1) * parseInt(tablePageSize, 10) + index;

                          return (
                            <tr key={row.acNo || index} style={{ borderBottom: "1px solid #f1f2f6" }}>
                              <td className="text-center fw-semibold text-muted">{globalIndex + 1}</td>
                              <td>
                                <span className="badge border fw-medium px-2 py-1" style={{ backgroundColor: "#f8f9fa", color: "#334155" }}>
                                  {row.acNo || `ID: ${globalIndex + 1}`}
                                </span>
                              </td>
                              <td>
                                <div className="fw-bold text-dark">{row.name}</div>
                                <small className="text-muted">S/o: {row.fatherName || "—"}</small>
                              </td>
                              <td>
                                {row.uan && String(row.uan).trim() !== "" && String(row.uan) !== "0" ? (
                                  <span className="badge fw-bold px-2 py-1" style={{ backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #86efac" }}>
                                    {row.uan}
                                  </span>
                                ) : (
                                  <span className="badge fw-normal px-2 py-1" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a" }}>
                                    Pending
                                  </span>
                                )}
                              </td>
                              <td className="text-end fw-semibold text-muted">
                                {row.paidDays.toFixed(2)}
                              </td>
                              <td className="text-end text-dark">
                                ₹{row.basic.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-end text-dark">
                                ₹{row.gross.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-end fw-semibold" style={{ color: "#0f172a" }}>
                                ₹{row.eligibleSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="text-end fw-bold" style={{ color: "#1e3c72" }}>
                                ₹{row.employeePf.toLocaleString('en-IN')}
                              </td>
                              <td className="text-end text-dark">
                                ₹{row.emplrAc1.toLocaleString('en-IN')}
                              </td>
                              <td className="text-end text-dark">
                                ₹{row.emplrAc10.toLocaleString('en-IN')}
                              </td>
                              <td className="text-end fw-bold" style={{ color: "#15803d" }}>
                                ₹{row.totalPf.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          );
                        })
                      )}

                      {/* Grand Totals */}
                      {filteredRows.length > 0 && (
                        <tr style={{ background: "#f8fafc", borderTop: "2px solid #cbd5e1", borderBottom: "2px solid #cbd5e1" }}>
                          <td colSpan={4} className="text-end fw-bold text-dark pe-3">
                            GRAND TOTALS:
                          </td>
                          <td className="text-end fw-bold text-muted">
                            {totals.totalPaidDays.toFixed(2)} Days
                          </td>
                          <td className="text-end fw-bold text-dark">
                            ₹{totals.totalBasic.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="text-end fw-bold text-dark">
                            ₹{totals.totalGross.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="text-end fw-bold" style={{ color: "#0f172a" }}>
                            ₹{totals.totalEligible.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="text-end fw-bold" style={{ color: "#1e3c72" }}>
                            ₹{totals.totalEmpPf.toLocaleString('en-IN')}
                          </td>
                          <td className="text-end fw-bold text-dark">
                            ₹{totals.totalAc1.toLocaleString('en-IN')}
                          </td>
                          <td className="text-end fw-bold text-dark">
                            ₹{totals.totalAc10.toLocaleString('en-IN')}
                          </td>
                          <td className="text-end fw-bold" style={{ color: "#15803d" }}>
                            ₹{totals.grandTotalPf.toLocaleString('en-IN')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Table Footer Pagination */}
                {tablePageSize !== "all" && totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
                    <small className="text-muted">
                      Page <strong>{tablePage}</strong> of <strong>{totalPages}</strong> ({filteredRows.length} total employees)
                    </small>
                    <div className="btn-group" role="group">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={tablePage === 1}
                        onClick={() => setTablePage(p => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={tablePage === totalPages}
                        onClick={() => setTablePage(p => Math.min(totalPages, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TAB 2: 🏛️ STATUTORY FORM 12 STATEMENT & SUMMARY           */}
          {/* ═══════════════════════════════════════════════════════════ */}
          {activeView === "statutory" && (
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center bg-white border-bottom py-3 px-4 gap-3">
                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    className="px-2 py-1 fw-semibold fs-12"
                    style={{ backgroundColor: "#ffffff", color: "#1e293b", border: "1px solid #cbd5e1" }}
                    onClick={() => setActiveView("register")}
                  >
                    ← Back to Register
                  </Button>
                  <div>
                    <h6 className="mb-0 text-dark fw-bold fs-15">
                      Statutory Form 12 Statement & Summary
                    </h6>
                    <small className="text-muted fs-11">
                      Monthly Return of Contributions under EPF Scheme, 1952 (Paragraph 38(2))
                    </small>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    className="px-3 fw-semibold fs-12 shadow-xs"
                    style={{ backgroundColor: "#dc2626", color: "#ffffff", border: "none" }}
                    onClick={exportToPDF}
                  >
                    <i className="fa fa-file-pdf-o me-1 text-white" /> Download PDF
                  </Button>
                  <Button
                    size="sm"
                    className="px-3 fw-semibold fs-12 shadow-xs"
                    style={{ backgroundColor: "#2563eb", color: "#ffffff", border: "none" }}
                    onClick={() => window.print()}
                  >
                    <i className="fa fa-print me-1 text-white" /> Print Statement
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-4" style={{ background: "#f4f6f9" }}>
                <div
                  className="bg-white p-4 p-md-5 mx-auto shadow-sm border"
                  style={{
                    maxWidth: "860px",
                    borderRadius: "4px",
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    fontSize: "12px",
                    lineHeight: "1.6",
                    color: "#212529"
                  }}
                >
                  {/* Official EPFO Form 12 Header */}
                  <div className="text-center pb-3 border-bottom mb-4">
                    <div className="text-uppercase fw-bold text-muted mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                      Employees' Provident Fund Organisation (EPFO)
                    </div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      Employees' Provident Funds Scheme, 1952 (Paragraph 38(2)) & Employees' Pension Scheme, 1995
                    </div>
                    <h5 className="fw-bold mt-2 mb-1 text-dark" style={{ textDecoration: "underline" }}>
                      FORM NO. 12 (REVISED) - Monthly Statement of Contributions
                    </h5>
                    <div className="text-muted fs-11 mt-1">
                      (Monthly return to be submitted by the employer along with the Monthly Challan copy)
                    </div>
                  </div>

                  {/* Establishment Header Banner */}
                  <div className="p-3 mb-4 rounded border" style={{ background: "#f8f9fa", borderLeft: "4px solid #1e3c72" }}>
                    <Row className="g-2">
                      <Col sm={8}>
                        <div className="text-muted fs-11 text-uppercase fw-bold">Name of Establishment:</div>
                        <div className="fw-bold fs-13 text-dark">{sessionFirm?.Name || "ESTABLISHMENT NAME"}</div>
                        <div className="text-muted fs-11 mt-1">{getFirmAddress()}</div>
                      </Col>
                      <Col sm={4} className="text-sm-end">
                        <div className="text-muted fs-11 text-uppercase fw-bold">Establishment PF Code:</div>
                        <div className="fw-bold fs-13" style={{ color: "#1e3c72" }}>{getFirmPfNo()}</div>
                        <div className="text-muted fs-11 mt-1">
                          Period: <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Part I & II Schedules */}
                  <Row className="g-4 mb-4">
                    {/* Left: Wages Breakdown */}
                    <Col md={6}>
                      <div className="border rounded p-3 h-100 bg-white shadow-xs">
                        <h6 className="fw-bold text-dark fs-12 mb-3 pb-2 border-bottom text-uppercase" style={{ color: "#1e3c72" }}>
                          Part I: Wages & Membership Schedule
                        </h6>
                        <Table size="sm" borderless className="mb-0 align-middle" style={{ fontSize: "12px" }}>
                          <thead>
                            <tr className="border-bottom text-muted">
                              <th>Category</th>
                              <th className="text-end fw-bold">Wages (₹)</th>
                              <th className="text-end fw-bold">Workers</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="fw-semibold text-secondary">PF Applicable</td>
                              <td className="text-end text-dark">{Math.round(pfApplicableWages || totals.totalEligible).toLocaleString('en-IN')}</td>
                              <td className="text-end text-dark fw-bold">{pfApplicableNos || filteredRows.length}</td>
                            </tr>
                            <tr>
                              <td className="fw-semibold text-secondary">PF Exempt</td>
                              <td className="text-end text-dark">{Math.round(pfExemptWages).toLocaleString('en-IN')}</td>
                              <td className="text-end text-muted">—</td>
                            </tr>
                            <tr>
                              <td className="fw-semibold text-secondary">Exempt Workers</td>
                              <td className="text-end text-dark">{Math.round(exemptWages).toLocaleString('en-IN')}</td>
                              <td className="text-end text-dark fw-bold">{exemptNos}</td>
                            </tr>
                            <tr>
                              <td className="fw-semibold text-secondary">Absent / Leave</td>
                              <td className="text-end text-dark">0</td>
                              <td className="text-end text-dark fw-bold">{absentNos}</td>
                            </tr>
                            <tr className="border-top fw-bold text-dark" style={{ background: "#f8fafc" }}>
                              <td>Total Remittance Wages:</td>
                              <td className="text-end" style={{ color: "#1e3c72" }}>₹{Math.round(totalSummaryWages || totals.totalEligible).toLocaleString('en-IN')}</td>
                              <td className="text-end" style={{ color: "#1e3c72" }}>{totalSummaryNos || filteredRows.length}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>

                    {/* Right: Statutory Dues Distribution */}
                    <Col md={6}>
                      <div className="border rounded p-3 h-100 bg-white shadow-xs">
                        <h6 className="fw-bold text-dark fs-12 mb-3 pb-2 border-bottom text-uppercase" style={{ color: "#1e3c72" }}>
                          Part II: Statutory Dues Distribution
                        </h6>
                        <Table size="sm" borderless className="mb-0 align-middle" style={{ fontSize: "12px" }}>
                          <tbody>
                            <tr>
                              <td className="text-secondary fw-semibold">Employee + Employer EPF (A/c No. 1)</td>
                              <td className="text-end text-dark fw-bold">₹{empPlusEmplrAc1.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <td className="text-secondary fw-semibold">Employers Pension Fund (A/c No. 10)</td>
                              <td className="text-end text-dark fw-bold">₹{pensionAc10.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <td className="text-secondary fw-semibold">EPF Admin Charges (A/c No. 2)</td>
                              <td className="text-end text-dark fw-bold">₹{adminAc2.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <td className="text-secondary fw-semibold">EDLI Contribution (A/c No. 21)</td>
                              <td className="text-end text-dark fw-bold">₹{edliAc21.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr>
                              <td className="text-secondary fw-semibold">EDLI Admin Charges (A/c No. 22)</td>
                              <td className="text-end text-dark fw-bold">₹{insuranceAc22.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            <tr className="border-top fw-bold text-dark" style={{ background: "#f8fafc" }}>
                              <td className="text-end pe-2">Grand Total Dues Payable:</td>
                              <td className="text-end" style={{ color: "#15803d", fontSize: "13px" }}>₹{rightTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                  </Row>

                  {/* Part III: Statutory Employer Certification */}
                  <div className="border p-3 rounded bg-white mb-2" style={{ borderLeft: "4px solid #15803d" }}>
                    <h6 className="fw-bold text-dark fs-12 mb-2">DECLARATION & CERTIFICATION BY EMPLOYER</h6>
                    <p className="mb-2 text-dark" style={{ fontSize: "11.5px", lineHeight: "1.5" }}>
                      Certified that the amount of wages, recoveries, and employer's contributions shown in this statement are correct and agree with the wage register and statutory payroll records of the establishment. The dues have been paid through prescribed electronic challan for the period of <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong>.
                    </p>
                    <Row className="mt-4 pt-3 border-top text-dark" style={{ fontSize: "11px" }}>
                      <Col xs={4}>
                        <div><strong>Date:</strong> ____________________</div>
                        <div className="mt-2"><strong>Place:</strong> Jodhpur</div>
                      </Col>
                      <Col xs={8} className="text-end">
                        <div style={{ height: "35px" }} />
                        <div className="fw-bold border-top d-inline-block pt-1 px-4">
                          Signature of Employer with Seal of Establishment
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* ─── PHYSICAL PRINTING VIEW ─── */}
          <div id="print-form12-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
              {sessionFirm?.Name?.toUpperCase()}
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px", margin: "4px 0" }}>
              EMPLOYEES PROVIDENT FUND REPORT (FORM 12) FOR THE MONTH OF &nbsp;
              <span style={{ textDecoration: "underline" }}>{(selectedMonth || "").toUpperCase()} {selectedYear}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", margin: "6px 0", borderBottom: "1px solid #000", paddingBottom: "4px" }}>
              <div><strong>CODE NO.:</strong> {getFirmPfNo()}</div>
              <div><strong>FORM NO. 12 (REVISED)</strong></div>
            </div>

            <table className="form12-print-table">
              <thead>
                <tr>
                  <th rowSpan={2}>Sr. No.</th>
                  <th>AC No</th>
                  <th>EMPLOYEE NAME</th>
                  <th>BASIC</th>
                  <th>GROSS WAGES</th>
                  <th rowSpan={2}>EMPLOYEE P.F.</th>
                  <th colSpan={2}>EMPLOYER'S SHARE</th>
                </tr>
                <tr>
                  <th>UAN</th>
                  <th>FATHER NAME</th>
                  <th>PAID DAYS</th>
                  <th>ELIGIBLE SALARY</th>
                  <th>A/C.1 P.F.</th>
                  <th>A/C.10 PENSION</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <Fragment key={row.sno}>
                    <tr>
                      <td rowSpan={2} style={{ textAlign: "center" }}>{row.sno}</td>
                      <td style={{ textAlign: "center" }}>{row.acNo}</td>
                      <td><strong>{row.name}</strong></td>
                      <td style={{ textAlign: "right" }}>₹{row.basic.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>₹{row.gross.toFixed(2)}</td>
                      <td rowSpan={2} style={{ textAlign: "right", fontWeight: "bold" }}>₹{row.employeePf}</td>
                      <td style={{ textAlign: "right" }}>₹{row.emplrAc1}</td>
                      <td style={{ textAlign: "right" }}>₹{row.emplrAc10}</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center", fontSize: "9px" }}>{row.uan}</td>
                      <td style={{ fontSize: "9px" }}>{row.fatherName}</td>
                      <td style={{ textAlign: "right" }}>{row.paidDays.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>₹{row.eligibleSalary.toFixed(2)}</td>
                      <td style={{ textAlign: "center" }}>—</td>
                      <td style={{ textAlign: "center" }}>—</td>
                    </tr>
                  </Fragment>
                ))}

                <tr style={{ fontWeight: "bold", background: "#f2f2f2" }}>
                  <td colSpan={3} style={{ textAlign: "right" }}>TOTALS:</td>
                  <td style={{ textAlign: "right" }}>₹{totals.totalBasic.toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>₹{totals.totalGross.toFixed(2)}</td>
                  <td style={{ textAlign: "right" }}>₹{totals.totalEmpPf}</td>
                  <td style={{ textAlign: "right" }}>₹{totals.totalAc1}</td>
                  <td style={{ textAlign: "right" }}>₹{totals.totalAc10}</td>
                </tr>
              </tbody>
            </table>

            {/* Print Summary Schedule */}
            <div style={{ display: "flex", gap: "20px", marginTop: "15px" }}>
              <table style={{ width: "48%", borderCollapse: "collapse", fontSize: "10px" }}>
                <thead>
                  <tr style={{ background: "#eee" }}>
                    <th style={{ border: "1px solid #000", padding: "3px" }}>Category</th>
                    <th style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>Wages (₹)</th>
                    <th style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>Nos</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>PF Applicable</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>{Math.round(pfApplicableWages || totals.totalEligible)}</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>{pfApplicableNos || filteredRows.length}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>PF Exempt</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>{Math.round(pfExemptWages)}</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>—</td>
                  </tr>
                  <tr style={{ fontWeight: "bold" }}>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>Total :</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>₹{Math.round(totalSummaryWages || totals.totalEligible)}</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>{totalSummaryNos || filteredRows.length}</td>
                  </tr>
                </tbody>
              </table>

              <table style={{ width: "48%", borderCollapse: "collapse", fontSize: "10px" }}>
                <tbody>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>A/c No. 1 (Emp + Emplr Share)</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right", fontWeight: "bold" }}>₹{empPlusEmplrAc1.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>A/c No. 10 (Pension Fund)</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right", fontWeight: "bold" }}>₹{pensionAc10.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>A/c No. 2 (Admin Charges)</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right", fontWeight: "bold" }}>₹{adminAc2.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>A/c No. 21 (EDLI)</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right", fontWeight: "bold" }}>₹{edliAc21.toFixed(2)}</td>
                  </tr>
                  <tr style={{ fontWeight: "bold", background: "#eee" }}>
                    <td style={{ border: "1px solid #000", padding: "3px" }}>Grand Total Payable :</td>
                    <td style={{ border: "1px solid #000", padding: "3px", textAlign: "right" }}>₹{rightTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", fontSize: "11px" }}>
              <div>Date: ____________________</div>
              <div>Signature of Employer with Seal: ____________________</div>
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default PfForm12Report;
